import { supabase } from '../config/supabase';
import { TMDB_API_KEY, TMDB_BASE_URL } from '../config/constants';
import { getTodayInPST, getYesterdayInPST } from '../utils/dateUtils';

export const saveGameData = async (userId, gameId, steps_taken, isGameComplete, endResult = null) => {
  try {
    const updateData = {
      steps_taken,
      end_result: endResult,
    };
    if (isGameComplete) {
      updateData.completed_at = new Date().toISOString();
      updateData.end_result = "completed";
    }
    const { error } = await supabase
      .from("Games")
      .update(updateData)
      .eq("id", gameId);
    if (error) throw error;
  } catch (error) {
    console.error("❌ Error saving game data:", error);
  }
};

export const saveCompletedReel = async ({
  userId,
  startMovie,
  endMovie,
  route,
  steps_taken,
  hintCount,
  rewindCount,
}) => {
  try {
    const actorChain = route.filter((item) => item.type === "actor").map((item) => item.name);
    const movieChain = route.filter((item) => item.type === "movie").map((item) => item.name);
    const { error } = await supabase.from("completed_reels").insert([
      {
        user_id: userId,
        start_movie: startMovie,
        end_movie: endMovie,
        actor_chain: actorChain,
        movie_chain: movieChain,
        frame_count: steps_taken,
        hints_used: hintCount,
        rewind_count: rewindCount,
        source: "reely",
      },
    ]);
    if (error) console.error("❌ Error saving completed reel:", error);
    else console.log("✅ Completed reel saved");
  } catch (err) {
    console.error("❌ Unexpected error saving reel:", err);
  }
};

export const updateUserStats = async (userId) => {
  const today = getTodayInPST();
  const yesterday = getYesterdayInPST();

  let { data: user, error: fetchError } = await supabase
    .from("usersv2")
    .select("last_completed_date, current_streak, longest_streak, total_reels_completed")
    .eq("user_id", userId)
    .maybeSingle();

  if (fetchError) {
    console.error("❌ Failed to fetch user for stats update:", fetchError.message);
    return 0;
  }

  if (!user) {
    await supabase.from("usersv2").insert([{ user_id: userId }]);
    const result = await supabase
      .from("usersv2")
      .select("last_completed_date, current_streak, longest_streak, total_reels_completed")
      .eq("user_id", userId)
      .single();
    user = result.data;
  }

  const lastCompleted = user.last_completed_date;
  const totalCompleted = (user.total_reels_completed || 0) + 1;
  let currentStreak = 1;
  let longestStreak = user.longest_streak || 0;

  if (lastCompleted === today) {
    currentStreak = user.current_streak || 1;
    longestStreak = Math.max(longestStreak, currentStreak);
  } else if (lastCompleted === yesterday) {
    currentStreak = (user.current_streak || 0) + 1;
    longestStreak = Math.max(longestStreak, currentStreak);
  } else {
    currentStreak = 1;
    longestStreak = Math.max(longestStreak, 1);
  }

  await new Promise((resolve) => setTimeout(resolve, 100));

  const { data: updatedRow, error: updateError } = await supabase
    .from("usersv2")
    .update({
      total_reels_completed: totalCompleted,
      current_streak: currentStreak,
      longest_streak: longestStreak,
      last_completed_date: today,
    })
    .eq("user_id", userId.toLowerCase())
    .select();

  if (updateError) {
    console.error("❌ Supabase update failed:", updateError.message);
  }

  return updatedRow?.[0]?.total_reels_completed || 0;
};

export const fetchUserStats = async (userId) => {
  const { data, error } = await supabase
    .from("usersv2")
    .select("*")
    .eq("user_id", userId)
    .single();
  if (error) {
    console.error("❌ Error fetching user stats:", error.message);
    return null;
  }
  return data;
};

export const fetchUserStreak = async (userId) => {
  const { data, error } = await supabase
    .from("usersv2")
    .select("current_streak")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) {
    console.error("❌ Failed to fetch streak:", error.message);
    return null;
  }
  return data?.current_streak ?? null;
};

export const fetchReelStats = async (userId) => {
  const { data, error } = await supabase
    .from("completed_reels")
    .select("frame_count")
    .eq("user_id", userId);
  if (error) {
    console.error("❌ Error fetching reel stats:", error.message);
    return null;
  }
  const frameCounts = data.map((e) => e.frame_count).filter((c) => typeof c === "number");
  if (frameCounts.length === 0) return { avg: 0, min: 0, max: 0 };
  const sum = frameCounts.reduce((acc, val) => acc + val, 0);
  return {
    avg: Number((sum / frameCounts.length).toFixed(2)),
    min: Math.min(...frameCounts),
    max: Math.max(...frameCounts),
  };
};

export const logActorMovieUsage = async (userId, name, type) => {
  try {
    const { error: userError } = await supabase.rpc('increment_user_actor_movie_usage', {
      input_user_id: userId,
      input_name: name,
      input_type: type,
    });
    if (userError) throw userError;

    const { error: globalError } = await supabase.rpc('increment_actor_movie_usage', {
      input_name: name,
      input_type: type,
    });
    if (globalError) throw globalError;
  } catch (error) {
    console.error("❌ Error logging actor/movie usage:", error);
  }
};

export const logPageVisit = async (userId) => {
  await supabase
    .from("PageVisits")
    .insert([{ user_id: userId, visited_at: new Date().toISOString() }])
    .then(({ error }) => {
      if (error) console.error("❌ Failed to log page visit:", error);
    });
};

export const startNewGame = async (userId) => {
  try {
    const { data, error } = await supabase
      .from("Games")
      .insert([{ user_id: userId, start_movie: null, end_movie: null, steps_taken: 0, completed_at: null }])
      .select("id")
      .single();
    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error("❌ Error creating game:", error);
    return null;
  }
};

export const logGameStart = async (gameId) => {
  try {
    await supabase
      .from("Games")
      .update({ game_started_at: new Date().toISOString() })
      .eq("id", gameId);
  } catch (error) {
    console.error("❌ Error logging game start:", error);
  }
};

export const fetchTodayMoviePair = async (gameDate) => {
  const { data, error } = await supabase
    .from("daily_movie_pair")
    .select("*")
    .eq("game_date", gameDate)
    .limit(1)
    .single();
  if (error) {
    console.error("❌ Error fetching today's movie pair:", error.message);
    return null;
  }
  return data;
};

export const fetchAndStoreStartMovieCast = async (startMovieId, gameDate) => {
  try {
    const { data: existingData, error: existingError } = await supabase
      .from("daily_movie_pair")
      .select("start_movie_cast")
      .eq("game_date", gameDate)
      .single();

    if (existingError) {
      console.error("❌ Error checking for existing cast:", existingError);
      return;
    }

    if (Array.isArray(existingData?.start_movie_cast) && existingData.start_movie_cast.length > 0) {
      return;
    }

    const url = `${TMDB_BASE_URL}/movie/${startMovieId}/credits?api_key=${TMDB_API_KEY}`;
    const response = await fetch(url);
    const json = await response.json();

    const topCast = json.cast
      .sort((a, b) => b.popularity - a.popularity)
      .slice(0, 15)
      .map((actor) => ({ name: actor.name, id: actor.id }));

    await supabase
      .from("daily_movie_pair")
      .update({ start_movie_cast: topCast })
      .eq("game_date", gameDate);
  } catch (error) {
    console.error("❌ Error fetching or storing start movie cast:", error);
  }
};

export const fetchAverageSteps = async (startMovie, endMovie) => {
  const { data, error } = await supabase.rpc("get_average_steps", {
    start_movie_input: startMovie,
    end_movie_input: endMovie,
  });
  if (error) return null;
  return data !== null ? parseFloat(data) : null;
};

export const fetchMinSteps = async (startMovie, endMovie) => {
  const { data, error } = await supabase.rpc("get_min_steps", {
    start_movie_input: startMovie,
    end_movie_input: endMovie,
  });
  if (error) return null;
  return data;
};

export const fetchTopActorAndMovie = async (userId) => {
  const { data, error } = await supabase
    .from("useractormovieusage")
    .select("name, type, count")
    .eq("user_id", userId)
    .order("count", { ascending: false })
    .order("name", { ascending: true });
  if (error) {
    console.error("❌ Error fetching top actor/movie:", error);
    return null;
  }
  const topActors = data.filter((d) => d.type === "actor").slice(0, 3);
  const topMovies = data.filter((d) => d.type === "movie").slice(0, 3);
  return { topActors, topMovies };
};

export const fetchUniversalTopActorsAndMovies = async () => {
  const { data, error } = await supabase.rpc("get_top_actors_and_movies");
  if (error) {
    console.error("❌ Error fetching universal stats:", error);
    return null;
  }
  const topActors = data.filter((d) => d.type === "actor").slice(0, 5);
  const topMovies = data.filter((d) => d.type === "movie").slice(0, 5);
  return { topActors, topMovies };
};
